import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent,
  CircularProgress, Alert, Stack,
} from '@mui/material';
import {
  Article, Send, TouchApp, AttachMoney, TrendingUp,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../components/AuthProvider';

const WORKERS_URL = 'https://share2dm-webhook.share2dm.workers.dev';

interface CreatorStats {
  contentCount: number;
  totalViews: number;
  totalLikes: number;
  dmsSent: number;
  clicks: number;
  totalEarnings: number;
  monthlyEarnings: Array<{ month: string; earnings: number }>;
}

export default function CreatorAnalytics() {
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const { user } = useAuth();
  const [stats, setStats] = useState<CreatorStats>({
    contentCount: 0, totalViews: 0, totalLikes: 0,
    dmsSent: 0, clicks: 0, totalEarnings: 0, monthlyEarnings: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      // seller_profile 조회
      const { data: sp } = await supabase
        .from('seller_profile').select('id').eq('user_id', user.id).single();
      if (!sp) throw new Error('프로필을 찾을 수 없습니다.');

      // 컨텐츠 통계
      const { data: contents } = await supabase
        .from('contents')
        .select('id, view_count, like_count')
        .eq('creator_id', sp.id);

      const contentCount = contents?.length ?? 0;
      const totalViews = contents?.reduce((s, c) => s + (c.view_count ?? 0), 0) ?? 0;
      const totalLikes = contents?.reduce((s, c) => s + (c.like_count ?? 0), 0) ?? 0;

      // DM 캠페인 통계 (share2dm_brands 통해)
      let dmsSent = 0, clicks = 0;
      const { data: brand } = await supabase
        .from('share2dm_brands').select('id').eq('clozet_seller_id', sp.id).maybeSingle();
      if (brand) {
        const { count: dCount } = await supabase
          .from('share2dm_dm_logs').select('*', { count: 'exact', head: true }).eq('brand_id', brand.id);
        const { count: cCount } = await supabase
          .from('share2dm_dm_logs').select('*', { count: 'exact', head: true })
          .eq('brand_id', brand.id).not('link_clicked_at', 'is', null);
        dmsSent = dCount ?? 0;
        clicks = cCount ?? 0;
      }

      // 제휴 수익
      const { data: earningsData } = await supabase
        .from('affiliate_earnings').select('amount, created_at').eq('creator_id', sp.id);

      const totalEarnings = earningsData?.reduce((s, e) => s + (e.amount ?? 0), 0) ?? 0;

      // 월별 수익
      const monthlyMap: Record<string, number> = {};
      earningsData?.forEach(e => {
        const m = new Date(e.created_at).toLocaleString('ko-KR', { month: 'short' });
        monthlyMap[m] = (monthlyMap[m] ?? 0) + (e.amount ?? 0);
      });
      const monthlyEarnings = Object.entries(monthlyMap).map(([month, earnings]) => ({ month, earnings }));

      setStats({ contentCount, totalViews, totalLikes, dmsSent, clicks, totalEarnings, monthlyEarnings });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const statCards = [
    { label: '업로드 컨텐츠', value: stats.contentCount, unit: '개', icon: <Article sx={{ fontSize: 28, color: '#7c3aed' }} /> },
    { label: '총 조회수', value: stats.totalViews.toLocaleString(), unit: '', icon: <TrendingUp sx={{ fontSize: 28, color: '#3b82f6' }} /> },
    { label: 'DM 발송', value: stats.dmsSent.toLocaleString(), unit: '건', icon: <Send sx={{ fontSize: 28, color: '#E1306C' }} /> },
    { label: '링크 클릭', value: stats.clicks.toLocaleString(), unit: '건', icon: <TouchApp sx={{ fontSize: 28, color: '#f59e0b' }} /> },
    { label: '총 수익', value: stats.totalEarnings.toLocaleString(), unit: '원', icon: <AttachMoney sx={{ fontSize: 28, color: '#10b981' }} /> },
  ];

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  );

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h5" fontWeight={700} mb={3} sx={styles.title}>
        분석
      </Typography>

      <Grid container spacing={2} mb={3}>
        {statCards.map(card => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.label}>
            <Card elevation={0} sx={{ ...styles.paper, borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5 }}>
                {card.icon}
                <Box>
                  <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h6" fontWeight={700} color={isDarkMode ? '#fff' : '#111'}>
                    {card.value}
                    {card.unit && (
                      <Typography component="span" variant="caption" ml={0.5} color="text.secondary">
                        {card.unit}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* DM 클릭률 */}
      {stats.dmsSent > 0 && (
        <Paper elevation={0} sx={{ ...styles.paper, p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2} color={isDarkMode ? '#fff' : '#111'}>
            DM 성과
          </Typography>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="h4" fontWeight={700} color="#E1306C">
                {((stats.clicks / stats.dmsSent) * 100).toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">클릭률 (CTR)</Typography>
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} color={isDarkMode ? '#fff' : '#111'}>
                {stats.dmsSent.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">총 DM 발송</Typography>
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} color="#10b981">
                {stats.clicks.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">링크 클릭</Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* 월별 수익 차트 */}
      {stats.monthlyEarnings.length > 0 && (
        <Paper elevation={0} sx={{ ...styles.paper, p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2} color={isDarkMode ? '#fff' : '#111'}>
            월별 수익
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={stats.monthlyEarnings}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#404040' : '#f0f0f0'} />
              <XAxis dataKey="month" stroke={isDarkMode ? '#aaa' : '#888'} />
              <YAxis stroke={isDarkMode ? '#aaa' : '#888'} tickFormatter={v => `₩${v.toLocaleString()}`} />
              <Tooltip
                contentStyle={{ backgroundColor: isDarkMode ? '#2d2d2d' : '#fff', border: 'none', borderRadius: 8 }}
                formatter={(v: any) => [`₩${Number(v).toLocaleString()}`, '수익']}
              />
              <Line type="monotone" dataKey="earnings" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {stats.monthlyEarnings.length === 0 && stats.dmsSent === 0 && (
        <Paper elevation={0} sx={{ ...styles.paper, p: 5, borderRadius: 3, textAlign: 'center' }}>
          <TrendingUp sx={{ fontSize: 48, color: isDarkMode ? '#555' : '#ddd', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} color="text.secondary">
            아직 데이터가 없습니다
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            컨텐츠를 업로드하고 DM 캠페인을 시작하면 통계가 표시됩니다.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
